import { Dispatch, SetStateAction, useState } from 'react';

import {
  PrimaryButton,
  TertiaryButton,
} from '@changegps/horizon-component-library';
import { Drawer, Stack, styled } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { useUserInfo } from '@/features/users';

import { updateSubscription } from '../../api';
import { subscriptionsQueries } from '../../data';
import { useBillingInfo } from '../../hooks';
import { useToastStore } from '../../store';
import { Subscription } from '../../types';
import { Modify } from '../content';

type SubscriptionData = (Subscription | null)[];

interface SubscriptionsDrawerProps {
  isVisible: boolean;
  toggleIsVisible: () => void;
  activeTab: number;
  setActiveTab: Dispatch<SetStateAction<number>>;
}

const StyledDrawer = styled(Drawer)(() => ({
  '& .MuiDrawer-paper': {
    width: '90vw',
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    boxShadow: 'none',
    borderTopLeftRadius: 16,
    borderBottomLeftRadius: 16,
    border: '1px solid #D8DBDF',
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0,0,0,0.3)',
  },
}));

export const SubscriptionsDrawer = ({
  isVisible,
  toggleIsVisible,
  activeTab,
  setActiveTab,
}: SubscriptionsDrawerProps) => {
  const { company_id, user_type } = useUserInfo();
  const { data } = useBillingInfo(company_id, user_type);
  const { showToast } = useToastStore();
  const queryClient = useQueryClient();

  const [currentSubscriptions, setCurrentSubscriptions] =
    useState<SubscriptionData>([]);
  const [isLoading, setIsLoading] = useState(false);

  const transformSubscriptionData = (subscriptions: SubscriptionData) => {
    return subscriptions
      .filter((sub) => sub !== null && sub !== undefined)
      .map((sub) => ({
        productid: Number(sub.product_id),
        tierid: Number(sub.tier_id),
        licences: [3, 5, 9].includes(Number(sub.tier_id))
          ? sub.licence_count
          : 3,
      }));
  };

  const handleSubscriptionChange = (subscriptions: SubscriptionData) => {
    setCurrentSubscriptions(subscriptions);
  };

  const handleConfirm = async () => {
    try {
      setIsLoading(true);

      const productInfo = transformSubscriptionData(currentSubscriptions);

      if (productInfo.length === 0) {
        toggleIsVisible();
        return;
      }

      await updateSubscription(company_id, productInfo);

      showToast('Subscription update successful!', 'success');

      toggleIsVisible();

      await queryClient.invalidateQueries({
        queryKey: [...subscriptionsQueries.getSubscriptions, company_id],
      });
      await queryClient.invalidateQueries({
        queryKey: [...subscriptionsQueries.getBillingInfo, company_id],
      });
    } catch (error) {
      showToast('Failed to update subscription. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setCurrentSubscriptions([]);
    toggleIsVisible();
  };

  return (
    <StyledDrawer open={isVisible} onClose={handleCancel} anchor="right">
      <Modify
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        initialSubscriptions={data?.future_subscriptions || []}
        onSubscriptionChange={handleSubscriptionChange}
      />
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        pt={8}
        px={2}
        pb={2}
      >
        <Stack spacing={1} flex={1}>
          <Stack direction={'row'} justifyContent={'space-between'}>
            <TertiaryButton onClick={handleCancel} disabled={isLoading}>
              Cancel
            </TertiaryButton>
            <PrimaryButton onClick={handleConfirm} disabled={isLoading}>
              {isLoading ? 'Updating...' : 'Confirm'}
            </PrimaryButton>
          </Stack>
        </Stack>
      </Stack>
    </StyledDrawer>
  );
};
